/**
 * TODO https://anchorfm.atlassian.net/browse/WHEEL-863: Should we create Anchor
 *  header components that wrap "Type"? In this file alone we have to repeat
 *  certain paddings and it might be nice to DRY up this code for more styling
 *  consistency.
 */
import { css } from 'emotion';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormGroup,
  Type,
  HorizontalRule,
  spacer24,
  spacer32,
  spacer16,
  FormHelpText,
  ButtonPrimary,
} from '@spotify-internal/encore-web';
import { FieldRadioToggle } from '@spotify-internal/anchor-ds';
import {
  ControlledFieldInput,
  ControlledFieldTextArea,
  ControlledFieldToggle,
  FieldInput,
  ControlledFieldSelect,
  FieldSelect,
  ControlledFieldHex,
} from 'components/encore/form/Field';
import { WarningIcon, WarningMessage } from 'components/WarningMessage';
import { useUnsavedChangesWarning } from 'contexts/UnsavedChangesWarning';
import { SocialUrlBody } from 'modules/AnchorAPI/updateSocialUrls';
import { getIsValidUrl } from 'modules/Url';
import { Tooltip, TOOLTIP_INFO_ID } from 'shared/Tooltip';
import { Metadata } from 'types/Metadata';
import { useFeatureFlagsCtx } from 'contexts/FeatureFlags';
import { getIsEPEnabled } from 'modules/FeatureFlags';
import { PodcastCategory } from 'modules/AnchorAPI/podcast/fetchPodcastCategories';
import {
  NarrowColumn,
  TwoColumnLayout,
} from 'components/encore/layouts/TwoColumnLayout';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
import { useLanguagesList } from 'client/i18n/hooks/useLanguagesList';
import { useFetchPodcastCategories } from 'client/hooks/podcast/useFetchPodcastCategories';
import { SpotifyAccountCard } from 'client/components/SpotifyOAuth/components/SpotifyAccountCard';
import { useOptimizelyFeature } from 'client/hooks/useOptimizelyFeature';
import { isValidEmail } from '../../../../user';
import { DeleteAccount } from '../DeleteAccount';
import { PendingEmailVerification } from '../PendingEmailVerification';
import { PodcastCoverArt } from './PodcastCoverArt';
import { SocialConnection } from './SocialConnection';
import { ERROR_MESSAGES } from './constants';
import {
  CustomURLLabel,
  Form,
  HeadingContainer,
  HorizontalFormGroup,
  MobileSaveContainer,
  MobileSaveErrorText,
  Section,
  PendingEmailContainer,
} from './styles';
import { NavigatedFromParam } from '../../types';

const UNSAVED_CHANGES_KEY = 'settingsForm';

type SubmissionData = Metadata & SocialUrlBody;
// This input can only accept string values, while we want to submit a boolean
type FormData = Omit<SubmissionData, 'isExplicit'> & { isExplicit?: string };

type SubCategory = {
  value: string;
  display: string;
};

type SettingsFormProps = {
  userId: number;
  userEmailValue: string;
  hasEpisodes: boolean;
  navigatedFrom: NavigatedFromParam;
  onSubmit: (data: SubmissionData) => Promise<SubmissionData>;
  initialValues: SubmissionData;
};

export function EncoreSettingsFormView({
  userId,
  onSubmit,
  userEmailValue,
  hasEpisodes,
  initialValues: {
    podcastImage,
    podcastImageFull,
    isExplicit,
    ...defaultValues
  },
  navigatedFrom,
}: SettingsFormProps) {
  const formData: FormData = {
    ...defaultValues,
    isExplicit: isExplicit?.toString() || 'false',
  };
  const {
    formState: { isDirty, isSubmitting, errors, isSubmitSuccessful },
    handleSubmit,
    register,
    setFocus,
    setError,
    control,
    reset,
    watch,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: formData,
    shouldFocusError: true,
  });
  const t = useT();

  const errorLength = !!Object.keys(errors).length;

  const buttonLabels = {
    SAVE: t('Button.Save', 'Save', 'Save button text'),
    SAVING: t('Button.Saving', 'Saving...', 'Saving button text'),
    SAVED: t('Button.Saved', 'Saved!', 'Saved button text'),
  };

  // For when your inputs are valid, but the API has returned an error.
  // This is put in state because it shouldn't prevent you from submitting
  // the form, since you won't necessairly have to change an input to resubmit.
  const [formError, setFormError] = useState(false);

  const [buttonCopy, setButtonCopy] = useState(buttonLabels.SAVE);

  useEffect(() => {
    if (isSubmitting && !errorLength && !formError) {
      setButtonCopy(buttonLabels.SAVING);
    } else if (isSubmitSuccessful && !isDirty) {
      setButtonCopy(buttonLabels.SAVED);
      setTimeout(() => setButtonCopy(buttonLabels.SAVE), 2000);
    } else {
      setButtonCopy(buttonLabels.SAVE);
    }
  }, [
    isSubmitting,
    errorLength,
    formError,
    isSubmitSuccessful,
    isDirty,
    buttonLabels.SAVE,
    buttonLabels.SAVING,
    buttonLabels.SAVED,
  ]);

  const {
    podcastCategoriesData: { podcastCategoryOptions = [] } = {},
  } = useFetchPodcastCategories();

  const {
    resetUnsavedChanges,
    addUnsavedKey,
    removeUnsavedKey,
  } = useUnsavedChangesWarning();
  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();

  const sortedLanguagesList = useLanguagesList();

  const isEPEnabled = getIsEPEnabled(featureFlags);

  const [isNewSpotifyOauthEnabled] = useOptimizelyFeature(
    'enable_new_spotify_oauth'
  );

  const fieldRequiredMessage = t(
    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.FieldRequiredMessage',
    "This can't be left empty.",
    'Field required warning message'
  );

  // Focus Podcast description field to catch user attention when Subscribers link is added
  useEffect(() => {
    if (navigatedFrom === 'subscribersLinkCTA') {
      setFocus('podcastDescription');
    }
  }, [navigatedFrom, setFocus]);

  // Reset unsaved changes state if the component unmounts
  useEffect(() => () => resetUnsavedChanges(), [resetUnsavedChanges]);

  const handleUnsavedChanges = useCallback(() => {
    if (isDirty) {
      addUnsavedKey(UNSAVED_CHANGES_KEY);
    } else {
      removeUnsavedKey(UNSAVED_CHANGES_KEY);
    }
  }, [addUnsavedKey, removeUnsavedKey, isDirty]);

  const hasChosenToHideEmail = (email: string) =>
    email.trim().endsWith('@privaterelay.appleid.com');

  useEffect(() => {
    handleUnsavedChanges();
  }, [handleUnsavedChanges]);

  const onValid = (data: FormData) => {
    setFormError(false);
    return onSubmit({ ...data, isExplicit: data.isExplicit === 'true' })
      .then(values => {
        reset({
          ...values,
          isExplicit: values.isExplicit?.toString() || 'false',
        });
      })
      .catch(({ message }) => {
        switch (message) {
          case ERROR_MESSAGES.EMAIL_TAKEN:
            setError('userEmail', {
              message: t(
                'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.EmailUsedInOtherAccountErrorMessage',
                'We found an existing account with this email. Try another!',
                'Error message used when trying to set in Podcast settings an email already in use in another account'
              ),
            });
            break;
          case ERROR_MESSAGES.VANITY_SLUG_TAKEN:
            setError('vanitySlug', {
              message: t(
                'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.AnchorURLNotAvailable',
                'This Anchor URL is not available.',
                'Error message used when trying to set an Anchor URL that is already in use'
              ),
            });
            break;
          case ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED:
            setError('podcastName', {
              message: ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED,
            });
            break;
          default:
            break;
        }
        setFormError(true);
      });
  };

  return (
    <Form method="POST" onSubmit={handleSubmit(onValid)} noValidate={true}>
      {(formError || errorLength) && (
        <WarningMessage
          className={css`
            margin-bottom: 60px;
            @media (max-width: 600px) {
              display: none;
            }
          `}
        >
          <WarningIcon>!</WarningIcon>
          {t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.SettingsDefaultErrorMessage',
            'Oops, looks like something went wrong. We were unable to save. Please try again.',
            'Default error message when saving podcast settings'
          )}
        </WarningMessage>
      )}

      <HeadingContainer>
        <Type as="h1" variant="altoBrio">
          {t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.SettingsHeader',
            'Podcast settings',
            'Podcast settings page header'
          )}
        </Type>
        <ButtonPrimary
          className={css`
            @media (max-width: 600px) {
              display: none;
            }
          `}
          type="submit"
          disabled={isSubmitting}
          data-cy="settingsSaveButtonDesktop"
        >
          {buttonCopy}
        </ButtonPrimary>
      </HeadingContainer>
      <HorizontalRule />

      <Section>
        <Type as="h2" variant="alto" paddingBottom={spacer32}>
          {t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastInfoHeader',
            'About your podcast',
            'Podcast information section header'
          )}
        </Type>
        <FormGroup
          label={t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastNameFieldLabel',
            'Podcast name',
            'Podcast name field label'
          )}
          labelFor="podcastName"
        >
          <ControlledFieldTextArea
            name="podcastName"
            id="podcastName"
            placeholder={t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastNameFieldPlaceholder',
              'Your podcast name is how people will find your podcast, both on Anchor and on other platforms.',
              'Podcast name field placholder'
            )}
            maxLength={100}
            rows={2}
            control={control}
            error={
              errors.podcastName?.message ===
              ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED
                ? {
                    message: t(
                      'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastNameFieldErrorMessage',
                      "You're a VIP! Please reach out to our {supportTeamLink} to finish your setup.",
                      'Podcast name field error message',
                      {
                        supportTeamLink: (
                          <a
                            key="support-link"
                            href={paths.getHelpPage('newRequest')}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t(
                              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastNameFieldErrorSupportTeamLinkText',
                              'support team',
                              'Support team link text'
                            )}
                          </a>
                        ),
                      }
                    ),
                  }
                : (errors.podcastName as any)
            }
          />
        </FormGroup>
        <FormGroup
          label={t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastDescriptionFieldLabel',
            'Podcast description',
            'Podcast description field label'
          )}
          labelFor="podcastDescription"
        >
          <ControlledFieldTextArea
            name="podcastDescription"
            id="podcastDescription"
            placeholder={t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastDescriptionFieldPlaceholder',
              'Tell people what your podcast is about. You can always change this later.',
              'podcast description field placeholder'
            )}
            maxLength={600}
            rows={5}
            control={control}
          />
        </FormGroup>

        <Type as="h3" variant="celloCanon" paddingBottom={spacer24}>
          {t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastCoverArtSectionHeader',
            'Podcast cover art',
            'Podcast cover art section header'
          )}
        </Type>
        <PodcastCoverArt
          podcastImage={podcastImage}
          podcastImageFull={podcastImageFull}
        />
      </Section>

      <Section>
        <TwoColumnLayout>
          <div>
            <Type as="h3" variant="celloCanon">
              {t(
                'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastDetailsSectionHeader',
                'Podcast details',
                'Podcast details section header'
              )}
            </Type>
            <HorizontalRule />
            <NarrowColumn>
              <FormGroup
                label={t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastCategoryFieldLabel',
                  'Podcast category',
                  'Podcast category field label'
                )}
                labelFor="itunesCategory"
              >
                <ControlledFieldSelect
                  name="itunesCategory"
                  id="itunesCategory"
                  options={mapPodcastCategories(podcastCategoryOptions)}
                  placeholder={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.FieldSelectPlaceholder',
                    'Choose one option...',
                    'Dropdown select field placeholder'
                  )}
                  control={control}
                />
              </FormGroup>
              <FormGroup
                label={t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastLanguageFieldLabel',
                  'Podcast language',
                  'Podcast language field label'
                )}
                labelFor="language"
              >
                <FieldSelect
                  {...register('language')}
                  id="language"
                  options={sortedLanguagesList}
                  placeholder={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.FieldSelectPlaceholder',
                    'Choose one option...',
                    'Dropdown select field placeholder'
                  )}
                />
              </FormGroup>
            </NarrowColumn>
          </div>
          <div>
            <Type as="h3" variant="celloCanon">
              {t(
                'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastAdvanceOptionsSectionHeader',
                'Advanced options',
                'Podcast advanced options section header'
              )}
            </Type>
            <HorizontalRule />

            <FormGroup
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <FieldRadioToggle aria-labelledby="isExplicit">
                <FieldRadioToggle.Label id="isExplicit">
                  {t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastContentFieldLabel',
                    'Content',
                    'Podcast content field label'
                  )}
                </FieldRadioToggle.Label>
                {[
                  {
                    label: t(
                      'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastContentCleanOptionLabel',
                      'Clean',
                      'Podcast content type option label'
                    ),
                    value: 'false',
                  },
                  {
                    label: t(
                      'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastContentExplicitOptionLabel',
                      'Explicit',
                      'Podcast content type option label'
                    ),
                    value: 'true',
                  },
                ].map(({ label, value }) => (
                  <FieldRadioToggle.Radio
                    {...register('isExplicit')}
                    value={value}
                    key={value}
                  >
                    {label}
                  </FieldRadioToggle.Radio>
                ))}
              </FieldRadioToggle>
            </FormGroup>

            {!isEPEnabled && (
              <div>
                <HorizontalRule style={{ marginTop: 0 }} />
                <ControlledFieldToggle
                  id="hasAnchorBranding"
                  name="hasAnchorBranding"
                  label={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.CoverArtShowAnchorLogoToggleLabel',
                    'Show Anchor logo on cover art',
                    'Show Anchor logo on cover art toggle text'
                  )}
                  control={control}
                />
                <ControlledFieldToggle
                  id="isPublicCallinShownOnWeb"
                  name="isPublicCallinShownOnWeb"
                  label={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.ShowVoiceMessagesInProfileToggleLabel',
                    'Show Voice Messages button on your Anchor profile',
                    'Label for toggle that allows enabling listeners Voice Messages in the podcast profile page'
                  )}
                  control={control}
                />
                <ControlledFieldToggle
                  id="isPublicCallinShownFromRSS"
                  name="isPublicCallinShownFromRSS"
                  label={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.IncludeVoiceMessagesLinkToggleLabel',
                    'Include Voice Messages link in your episode descriptions',
                    'Label for toggle that allows including Voice Messages links in episodes descriptions'
                  )}
                  control={control}
                />
              </div>
            )}
          </div>
        </TwoColumnLayout>

        <HorizontalRule />
      </Section>

      <Section>
        <Type as="h2" variant="alto" paddingBottom={spacer32}>
          {t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.AccountSettingsSectionHeader',
            'Account settings',
            'Account settings section header'
          )}
        </Type>
        <Type as="h3" variant="celloCanon" paddingBottom={spacer24}>
          {t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.AboutYouSectionHeader',
            'About you',
            'User about section header'
          )}
        </Type>

        <FormGroup
          label={t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.DisplayNameLabel',
            'Display name',
            'Label for name input'
          )}
          labelFor="authorName"
        >
          <ControlledFieldInput
            name="authorName"
            id="authorName"
            type="text"
            placeholder={t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.AuthorNameFieldPlaceholder',
              'My name',
              'Author name field placeholder'
            )}
            maxCharacterLength={80}
            showCharacterCount={true}
            rules={{
              required: fieldRequiredMessage,
              minLength: {
                value: 2,
                message: t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.AuthorNameFieldInvalidMessage',
                  'Name must have at least 2 characters.',
                  'Author name field invalid message'
                ),
              },
            }}
            control={control}
          />
        </FormGroup>
        <FormGroup
          label={
            <div>
              <span>
                {t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.EmailFieldLabel',
                  'Email',
                  'Email field label'
                )}
              </span>
              <PendingEmailContainer>
                <PendingEmailVerification userId={userId} />
              </PendingEmailContainer>
            </div>
          }
          labelFor="userEmail"
        >
          <FieldInput
            {...register('userEmail', {
              required: fieldRequiredMessage,
              validate: v =>
                isValidEmail(v) ||
                t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.EmailFieldInvalidMessage',
                  'This must be a valid email address.',
                  'Email field invalid message'
                ),
            })}
            error={errors.userEmail}
            id="userEmail"
            type="email"
            placeholder={t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.EmailFieldPlaceholder',
              'myname@mail.com',
              'Email field placeholder text'
            )}
            disabled={isEPEnabled}
          />
        </FormGroup>
        {userEmailValue && hasChosenToHideEmail(userEmailValue) && (
          <FormHelpText>
            {t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.AppleEmailMessage',
              'This forwarding email is provided by Apple. Changing your email address here will still allow you to Sign in with Apple.',
              'Email field value message'
            )}{' '}
          </FormHelpText>
        )}
        <FormGroup
          label={t(
            'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.WebsiteFieldLabel',
            'Website',
            'Author website field label'
          )}
          labelFor="userBioUrl"
        >
          <FieldInput
            {...register('userBioUrl', {
              validate: v => {
                if (!v) {
                  // Do not validate the input if null or empty
                  return true;
                }

                return (
                  getIsValidUrl(v) ||
                  t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.WebsiteFieldInvalidMessage',
                    'This must be a valid URL.',
                    'Author website field invalid message'
                  )
                );
              },
            })}
            error={errors.userBioUrl}
            name="userBioUrl"
            id="userBioUrl"
            placeholder={t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.WebsiteFieldPlaceholder',
              'https://mywebsite.com',
              'Author website field placeholder'
            )}
            type="url"
          />
        </FormGroup>
      </Section>
      {!isEPEnabled && (
        <Section>
          <TwoColumnLayout>
            <div>
              <CustomURLLabel as="h3" variant="celloCanon">
                {t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.CustomAnchorURLFieldLabel',
                  'Your custom URL',
                  'Custom Anchor URL field label'
                )}
                {!hasEpisodes && (
                  <Tooltip
                    text={t(
                      'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.CustomAnchorURLFieldTooltipMessage',
                      'Your URL is active once you publish your first episode',
                      'Custom Anchor URL field warning'
                    )}
                  />
                )}
              </CustomURLLabel>
              <HorizontalRule />
              <NarrowColumn>
                <HorizontalFormGroup label="anchor.fm/" labelFor="vanitySlug">
                  <ControlledFieldInput
                    name="vanitySlug"
                    id="vanitySlug"
                    maxCharacterLength={25}
                    showCharacterCount={true}
                    disabled={!hasEpisodes}
                    aria-describedby={!hasEpisodes ? TOOLTIP_INFO_ID : ''}
                    type="text"
                    rules={{
                      required: fieldRequiredMessage,
                      minLength: {
                        value: 2,
                        message: t(
                          'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.CustomAnchorURLFieldInvalidLengthMessage',
                          'URL must have at least 2 characters.',
                          'Custom Anchor URL field invalid message'
                        ),
                      },
                      pattern: {
                        value: /^[a-zA-Z0-9_-]*$/,
                        message: t(
                          'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.CustomAnchorURLFieldInvalidCharactersMessage',
                          "URL can't contain special characters.",
                          'Custom Anchor URL field invalid message'
                        ),
                      },
                    }}
                    control={control}
                  />
                </HorizontalFormGroup>
                <HorizontalFormGroup
                  label={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.ProfileColorFieldLabel',
                    'Profile color',
                    'Podcast profile color field label'
                  )}
                  indicator={t(
                    'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.ProfileColorFieldIndicator',
                    'hex code',
                    'Podcast profile color field code type indicator'
                  )}
                  labelFor="profileHeaderColor"
                >
                  <ControlledFieldHex
                    name="profileHeaderColor"
                    id="profileHeaderColor"
                    control={control}
                  />
                </HorizontalFormGroup>
              </NarrowColumn>
            </div>
            <div>
              <Type as="h3" variant="celloCanon">
                {t(
                  'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.PodcastConnectionsHeader',
                  'Connections',
                  'Podcast social connections section header'
                )}
              </Type>
              <HorizontalRule />
              {!isEPEnabled && (
                <div>
                  <SocialConnection
                    provider="twitter"
                    watch={watch}
                    register={register}
                  />
                  <SocialConnection
                    provider="instagram"
                    watch={watch}
                    register={register}
                  />
                  <SocialConnection
                    provider="youtube"
                    watch={watch}
                    register={register}
                  />
                  <SocialConnection
                    provider="facebook"
                    watch={watch}
                    register={register}
                  />
                  {false && isNewSpotifyOauthEnabled && (
                    <>
                      <Type
                        as="h4"
                        variant="ballad"
                        weight="bold"
                        paddingBottom={spacer16}
                      >
                        {t(
                          'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.SpotifyAccountHeader',
                          'Spotify Account',
                          'Spotify User Account Secion header'
                        )}
                      </Type>
                      <SpotifyAccountCard />
                    </>
                  )}
                </div>
              )}
            </div>
          </TwoColumnLayout>
        </Section>
      )}
      <HorizontalRule />
      {!isEPEnabled && <DeleteAccount />}
      <MobileSaveContainer>
        {errorLength && (
          <MobileSaveErrorText>
            {t(
              'SettingsScreen.EncoreSettingsForm.EncoreSettingsFormView.MobileSaveErrorText',
              'Something went wrong. Please try saving again.',
              'Podcast settings save error message'
            )}
          </MobileSaveErrorText>
        )}
        <ButtonPrimary
          type="submit"
          disabled={isSubmitting}
          data-cy="settingsSaveButtonMobile"
        >
          {buttonCopy}
        </ButtonPrimary>
      </MobileSaveContainer>
    </Form>
  );
}

function getOptions(options: SubCategory[]) {
  return options.map(({ display, value }) => ({
    label: display,
    value,
  }));
}

export function mapPodcastCategories(
  podcastCategoryOptions: PodcastCategory[]
) {
  return podcastCategoryOptions.map(category => {
    const { value, display, subCategories } = category;
    if (subCategories === undefined) {
      return {
        label: display,
        value,
      };
    }
    const subOptions = getOptions(subCategories);
    return {
      label: display,
      value,
      subOptions,
    };
  });
}
